/**
 * PipelineRowDetail — the expanded editor block for one pipeline.
 *
 * This is EXACTLY the editor PipelineRow used to render inline for every
 * pipeline, unchanged in behaviour and copy; the redesign only moved it behind
 * a per-row disclosure so the board can show ~50px summary lines by default.
 *
 *   1. xAI (Grok) dropdown         — xAI models valid for the pipeline's category,
 *                                    plus a "— none —" entry.
 *   2. Anthropic (Claude) dropdown — Claude models; DISABLED ("xAI only") for
 *                                    scoped_text_xai (safety), scoped_vision_noanthropic,
 *                                    scoped_realtime and fixed.
 *   3. Default dropdown            — the combined valid set; sets the pipeline default.
 * plus the chip cluster and the Use Default / Revert / History action row.
 *
 * Grok + Anthropic are mutually exclusive for the ACTIVE model: the active model
 * shows selected in whichever provider matches, and the other reads "— none —".
 * Picking a model in either provider re-points the active model via the parent's
 * onSetActive; the confirm + safety gate live on the page.
 *
 * `fixed` rows render all three controls disabled with a lock tooltip and offer
 * History only (their model is immutable — changing it would re-embed the corpus).
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { colors, typography } from '../../../theme/designTokens';
import { ModelCatalogRow, PipelineModel, PipelineModelRef } from '../../../services/quillAdminService';
import { categoryLabel, formatModelPricing } from './pipelineCatalog';
import { PipelineRowModel, NONE, enc, dec } from './pipelineRowModel';
import PipelineDefaultsBlock from './PipelineDefaultsBlock';
import { CounterbalanceBlock } from './CounterbalanceNotice';

const FIXED_WARNING =
  'This pipeline is fixed — its model is not admin-changeable. Changing it would require re-embedding the entire corpus and is deliberately locked.';

/**
 * Renders a catalog model as a menu entry: model_id + provider chip + pricing.
 *
 * The provider chip is the row's real `provider` column. It carries weight in the
 * combined Default dropdown, which is the one control that mixes both providers —
 * so it is colour-coded, not just labelled. (`?? 'xai'` remains only for a legacy
 * row with a NULL provider; the backend projects the column for every row.)
 */
const ModelOption: React.FC<{ row: ModelCatalogRow }> = ({ row }) => {
  const pricing = formatModelPricing(row);
  const provider = row.provider ?? 'xai';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Box component="span" sx={{ flexGrow: 1, fontFamily: typography.fonts.mono }}>
        {row.model_id}
      </Box>
      <Chip
        label={provider}
        size="small"
        variant="outlined"
        color={provider === 'anthropic' ? 'secondary' : 'primary'}
        sx={{ height: 18, fontSize: 10 }}
      />
      {pricing && (
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {pricing}
        </Typography>
      )}
    </Box>
  );
};

/**
 * A synthetic menu entry for a live model that has fallen out of the option list
 * (deprecated after it was set active/default, or a provider/category mismatch).
 * It carries the current encoded value so the Select always has a MenuItem
 * matching its value — no MUI "out-of-range value" warning, and the live model
 * stays visible and selectable-to-keep. Styled muted, with a warning chip.
 */
const SyntheticActiveOption: React.FC<{ value: string; note: string }> = ({ value, note }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', opacity: 0.8 }}>
    <Box component="span" sx={{ flexGrow: 1, fontFamily: typography.fonts.mono }}>
      {dec(value).model_id}
    </Box>
    <Chip label={note} size="small" variant="outlined" color="warning" sx={{ height: 18, fontSize: 10 }} />
  </Box>
);

/** Closed-control value: the model_id, plus a muted marker when it is off-catalog. */
const renderModelValue = (modelId: string, marker?: string): React.ReactNode => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
    <Box component="span" sx={{ fontFamily: typography.fonts.mono, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {modelId}
    </Box>
    {marker && (
      <Typography component="span" variant="caption" sx={{ color: 'warning.main', whiteSpace: 'nowrap' }}>
        {marker}
      </Typography>
    )}
  </Box>
);

export interface PipelineRowDetailProps {
  pipeline: PipelineModel;
  model: PipelineRowModel;
  onSetActive: (pipeline: PipelineModel, ref: PipelineModelRef) => void;
  onSetDefault: (pipeline: PipelineModel, ref: PipelineModelRef) => void;
  onUseDefault: (pipeline: PipelineModel) => void;
  onRevert: (pipeline: PipelineModel) => void;
  onHistory: (pipeline: PipelineModel) => void;
}

export const PipelineRowDetail: React.FC<PipelineRowDetailProps> = ({
  pipeline,
  model,
  onSetActive,
  onSetDefault,
  onUseDefault,
  onRevert,
  onHistory,
}) => {
  const { category, pipeline_key } = pipeline;
  const {
    isFixed,
    isSafety,
    anthropicAllowed,
    grokOptions,
    anthropicOptions,
    defaultOptions,
    active,
    grokValue,
    anthropicValue,
    defaultValue,
    grokMissing,
    anthropicMissing,
    defaultMissing,
    grokMarker,
    anthropicMarker,
    defaultMarker,
    activeNote,
    defaultNote,
  } = model;

  const handleActiveSelect = (value: string) => {
    if (value === NONE) return; // "none" is a display state, not an action
    onSetActive(pipeline, dec(value));
  };

  return (
    <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
      {/* Description + chip cluster */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1, minWidth: 220 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {pipeline.description}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
          {isSafety && (
            <Tooltip
              title="Safety-path pipeline — model changes require a vulnerable-user re-verification (G3)."
              arrow
            >
              <Chip
                icon={<HealthAndSafetyIcon sx={{ fontSize: 14 }} />}
                label="safety path"
                size="small"
                color="error"
                variant="outlined"
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          )}
          <Chip
            label={categoryLabel(category)}
            size="small"
            variant="outlined"
            color={isFixed ? 'default' : 'primary'}
          />
          <Chip
            label={pipeline.cost_center}
            size="small"
            sx={{ backgroundColor: colors.costs[50], color: colors.costs.dark, fontWeight: 600 }}
          />
          <Chip label={pipeline.volume_class} size="small" variant="outlined" />
        </Box>
      </Box>

      {/* Three controls */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 2,
          alignItems: 'flex-end',
        }}
      >
        {/* xAI (Grok) — active */}
        <FormControl size="small" fullWidth disabled={isFixed || grokOptions.length === 0}>
          <InputLabel id={`grok-label-${pipeline_key}`}>xAI (Grok)</InputLabel>
          <Select
            labelId={`grok-label-${pipeline_key}`}
            label="xAI (Grok)"
            value={isFixed ? enc(active.provider, active.model_id) : grokValue}
            onChange={(e) => handleActiveSelect(e.target.value as string)}
            renderValue={(v) => {
              const s = v as string;
              if (s === NONE) return <Typography sx={{ color: 'text.disabled' }}>— none —</Typography>;
              return renderModelValue(dec(s).model_id, grokMarker);
            }}
            inputProps={{ 'aria-label': `xAI model for ${pipeline_key}` }}
          >
            <MenuItem value={NONE}>
              <Typography sx={{ color: 'text.disabled' }}>— none —</Typography>
            </MenuItem>
            {isFixed && (
              <MenuItem value={enc(active.provider, active.model_id)} disabled>
                <Box sx={{ fontFamily: typography.fonts.mono }}>{active.model_id}</Box>
              </MenuItem>
            )}
            {grokMissing && (
              <MenuItem value={grokValue}>
                <SyntheticActiveOption value={grokValue} note={activeNote} />
              </MenuItem>
            )}
            {grokOptions.map((m) => (
              <MenuItem key={enc('xai', m.model_id)} value={enc('xai', m.model_id)}>
                <ModelOption row={m} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Anthropic (Claude) — active */}
        <Tooltip title={!anthropicAllowed && !isFixed ? 'xAI only — Anthropic is not permitted for this pipeline.' : ''} arrow>
          <FormControl size="small" fullWidth disabled={isFixed || !anthropicAllowed}>
            <InputLabel id={`anthropic-label-${pipeline_key}`}>Anthropic (Claude)</InputLabel>
            <Select
              labelId={`anthropic-label-${pipeline_key}`}
              label="Anthropic (Claude)"
              value={anthropicValue}
              onChange={(e) => handleActiveSelect(e.target.value as string)}
              renderValue={(v) => {
                const s = v as string;
                // Show an actively-set Anthropic model even when the category
                // nominally disables Anthropic — otherwise the live model would
                // hide behind "xAI only" (D8 mismatch edge).
                if (s !== NONE) return renderModelValue(dec(s).model_id, anthropicMarker);
                if (!anthropicAllowed) return <Typography sx={{ color: 'text.disabled' }}>xAI only</Typography>;
                return <Typography sx={{ color: 'text.disabled' }}>— none —</Typography>;
              }}
              inputProps={{ 'aria-label': `Anthropic model for ${pipeline_key}` }}
            >
              <MenuItem value={NONE}>
                <Typography sx={{ color: 'text.disabled' }}>— none —</Typography>
              </MenuItem>
              {anthropicMissing && (
                <MenuItem value={anthropicValue}>
                  <SyntheticActiveOption value={anthropicValue} note={activeNote} />
                </MenuItem>
              )}
              {anthropicOptions.map((m) => (
                <MenuItem key={enc('anthropic', m.model_id)} value={enc('anthropic', m.model_id)}>
                  <ModelOption row={m} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        {/* Default — combined */}
        <FormControl size="small" fullWidth disabled={isFixed || defaultOptions.length === 0}>
          <InputLabel id={`default-label-${pipeline_key}`}>Default</InputLabel>
          <Select
            labelId={`default-label-${pipeline_key}`}
            label="Default"
            value={isFixed ? '' : defaultValue}
            displayEmpty
            onChange={(e) => {
              const v = e.target.value as string;
              if (!v) return;
              onSetDefault(pipeline, dec(v));
            }}
            renderValue={(v) => {
              const s = v as string;
              if (!s) return <Typography sx={{ color: 'text.disabled' }}>—</Typography>;
              return renderModelValue(dec(s).model_id, defaultMarker);
            }}
            inputProps={{ 'aria-label': `Default model for ${pipeline_key}` }}
          >
            {defaultMissing && (
              <MenuItem value={defaultValue}>
                <SyntheticActiveOption value={defaultValue} note={defaultNote} />
              </MenuItem>
            )}
            {defaultOptions.map((m) => (
              <MenuItem key={enc(m.provider ?? 'xai', m.model_id)} value={enc(m.provider ?? 'xai', m.model_id)}>
                <ModelOption row={m} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* What the defaults ARE, and whether the live model matches them. */}
      <PipelineDefaultsBlock pipeline={pipeline} isFixed={isFixed} onUseDefault={onUseDefault} />

      {/* The chains this pipeline is a role in — shown healthy or collapsed, so
          the arrangement is met before it is broken. */}
      <CounterbalanceBlock pipeline={pipeline} />

      {/* Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        {isFixed ? (
          <Tooltip title={FIXED_WARNING} placement="top" arrow>
            <Chip icon={<LockIcon sx={{ fontSize: 16 }} />} label="fixed" size="small" sx={{ cursor: 'help' }} />
          </Tooltip>
        ) : (
          // Use Default lives in the defaults block above, beside the two model
          // ids it reconciles — one control, at the place the discrepancy is
          // read. It used to sit here as a permanently-visible button that was
          // disabled whenever the pipeline was already on its default.
          <Button
            size="small"
            variant="text"
            startIcon={<SettingsBackupRestoreIcon />}
            onClick={() => onRevert(pipeline)}
            sx={{ textTransform: 'none' }}
          >
            Revert
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          variant="text"
          startIcon={<HistoryIcon />}
          onClick={() => onHistory(pipeline)}
          sx={{ textTransform: 'none' }}
        >
          History
        </Button>
      </Box>
    </Box>
  );
};

export default PipelineRowDetail;
